import { useMemo, useState } from 'react'
import type { ConnectionStatus } from '../../../hooks/use-sse-stream.ts'
import type { FreeformUploadEvent } from '../../../utils/api-client/sse-schemas.ts'
import { useFreeformUploadEvents } from '../use-freeform-upload-events.ts'
import { type FreeformRunState, foldEvents } from './freeform-run.ts'

export type UseFreeformRun = {
  run: FreeformRunState
  /**
   * The last seq of the first batch this viewer received. Everything up to it is history a reload replayed,
   * drawn as it stands; only what arrives after it animates in.
   */
  settledSeq: number
  connectionStatus: ConnectionStatus
}

const NO_EVENTS: readonly FreeformUploadEvent[] = []

/** Whether `next` only appended to `previous`, so both came from one connection. */
const isAppendOf = (next: readonly FreeformUploadEvent[], previous: readonly FreeformUploadEvent[]) =>
  next.length >= previous.length && (previous.length === 0 || next[previous.length - 1] === previous.at(-1))

type Received = {
  sessionId: string | undefined
  /** What the connections before the current one delivered, in order. */
  earlier: readonly FreeformUploadEvent[]
  /** The current connection's events, as last seen. */
  latest: readonly FreeformUploadEvent[]
}

/**
 * Every event the session's connections delivered, in order. A reconnect resumes after the last event
 * received, so each connection starts with an empty buffer; the earlier connections' events are kept
 * rather than the run rewinding to what the new one has sent so far.
 */
const useEventsAcrossConnections = (sessionId: string | undefined, events: readonly FreeformUploadEvent[]) => {
  const [received, setReceived] = useState<Received>({ sessionId, earlier: [], latest: events })
  // Derived during render (React's "adjust state when a prop changes" pattern), so an event shows up in the
  // same commit it arrives in.
  if (received.sessionId !== sessionId) {
    setReceived({ sessionId, earlier: [], latest: events })
  } else if (events !== received.latest) {
    const earlier = isAppendOf(events, received.latest) ? received.earlier : [...received.earlier, ...received.latest]
    setReceived({ sessionId, earlier, latest: events })
  }
  const earlier = received.sessionId === sessionId ? received.earlier : NO_EVENTS
  return useMemo(() => (earlier.length > 0 ? [...earlier, ...events] : events), [earlier, events])
}

/** The seq of the first batch the viewer received, settled in the same commit it arrives in so it never animates. */
const useSettledSeq = (sessionId: string | undefined, run: FreeformRunState) => {
  const [settled, setSettled] = useState<{ sessionId: string | undefined; seq: number }>({ sessionId, seq: 0 })
  if (settled.sessionId !== sessionId) {
    setSettled({ sessionId, seq: run.seq })
  } else if (settled.seq === 0 && run.seq > 0) {
    setSettled({ sessionId, seq: run.seq })
  }
  return settled.sessionId === sessionId ? settled.seq : 0
}

/**
 * Follows a Findings Intake run's stream and folds it into run state. The stream reconnects on its own
 * while the run is unfinished, resuming after the last event received; the run carries on from where it
 * was. Pass `undefined` to stay idle.
 */
export const useFreeformRun = ({
  organizationId,
  sessionId,
}: {
  organizationId?: string
  sessionId: string | undefined
}): UseFreeformRun => {
  const { events, connectionStatus } = useFreeformUploadEvents({ organizationId, sessionId })
  const received = useEventsAcrossConnections(sessionId, events)
  const run = useMemo(() => foldEvents(received), [received])
  const settledSeq = useSettledSeq(sessionId, run)
  return { run, settledSeq, connectionStatus }
}
